import React, { Component, ErrorInfo, ReactNode } from 'react';
import ErrorReportingService from '../common/ErrorReportingService';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error and send to Pub/Sub via API
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // Use the centralized error reporting service
    this.sendErrorToPubSub(error, errorInfo);
  }

  sendErrorToPubSub = async (error: Error, errorInfo: ErrorInfo) => {
    const errorReporting = ErrorReportingService.getInstance();
    
    // Report the React error boundary catch
    errorReporting.sendErrorToPubSub({
      type: 'React Error Boundary',
      message: error.message,
      stack: error.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      customData: {
        errorName: error.name,
        componentStack: errorInfo.componentStack,
        reactErrorBoundary: true
      }
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          backgroundColor: '#f8d7da', 
          color: '#721c24',
          minHeight: '50vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <h1>🚨 Something went wrong</h1>
          <p>We're sorry, but something unexpected happened. Our team has been notified.</p>
          <button 
            onClick={() => {
              this.setState({ hasError: false, error: undefined, errorInfo: undefined });
              window.location.reload();
            }}
            style={{
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: 'pointer',
              marginTop: '20px'
            }}
          >
            Reload Page
          </button>
          
          {process.env.NODE_ENV === 'development' && (
            <details style={{ marginTop: '20px', textAlign: 'left', maxWidth: '80%' }}>
              <summary>Error Details (Development)</summary>
              <pre style={{ backgroundColor: '#f8f9fa', padding: '10px', overflow: 'auto' }}>
                {this.state.error && this.state.error.toString()}
                <br />
                {this.state.errorInfo && this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
